import React, { useState, useEffect } from "react";
import styles from "./GradeCalculator.module.css"; // optional CSS file

const MAX_ROWS = 12;

const getGradeAndPoints = (marks) => {
    if (marks >= 90) return { grade: "A+", points: 4.0 };
    if (marks >= 80) return { grade: "A", points: 4.0 };
    if (marks >= 75) return { grade: "B+", points: 3.5 };
    if (marks >= 70) return { grade: "B", points: 3.0 };
    if (marks >= 65) return { grade: "C+", points: 2.5 };
    if (marks >= 60) return { grade: "C", points: 2.0 };
    if (marks >= 55) return { grade: "D+", points: 1.5 };
    if (marks >= 50) return { grade: "D", points: 1.0 };
    return { grade: "F", points: 0.0 };
};

const GradeCalculator = () => {
    const [rows, setRows] = useState([]);
    const [currentGpa, setCurrentGpa] = useState("");
    const [totalCreditHours, setTotalCreditHours] = useState("");
    const [summary, setSummary] = useState({
        sumCreditHours: 0,
        sumGradePoints: 0,
        semesterGpa: 0,
        cgpa: 0,
    });

    // initialize with 6 rows
    useEffect(() => {
        resetCalculator();
    }, []);

    const handleRowChange = (index, field, value) => {
        const updatedRows = [...rows];
        updatedRows[index][field] = value;

        // auto update grade + points
        if (field === "marks" || field === "creditHours") {
            const marks = parseFloat(updatedRows[index].marks) || 0;
            const creditHours = parseFloat(updatedRows[index].creditHours) || 0;
            const { grade, points } = getGradeAndPoints(marks);
            updatedRows[index].grade = grade;
            updatedRows[index].points = points;
            updatedRows[index].gradePoints = (points * creditHours).toFixed(2);
        }

        // auto add new row if last one has input
        if (
            index === rows.length - 1 &&
            (updatedRows[index].marks || updatedRows[index].creditHours) &&
            rows.length < MAX_ROWS
        ) {
            updatedRows.push(createRow(rows.length + 1));
        }

        setRows(updatedRows);
    };

    const calculate = () => {
        let sumGradePoints = 0;
        let sumCreditHours = 0;

        rows.forEach((row) => {
            const gradePoints = parseFloat(row.gradePoints) || 0;
            const creditHours = parseFloat(row.creditHours) || 0;
            sumGradePoints += gradePoints;
            sumCreditHours += creditHours;
        });

        const semesterGpa = sumCreditHours ? sumGradePoints / sumCreditHours : 0;
        let cgpa = 0;
        if (currentGpa && totalCreditHours) {
            cgpa =
                (parseFloat(currentGpa) * parseFloat(totalCreditHours) + sumGradePoints) /
                (parseFloat(totalCreditHours) + sumCreditHours);
        }

        setSummary({
            sumCreditHours,
            sumGradePoints: sumGradePoints.toFixed(3),
            semesterGpa: semesterGpa.toFixed(4),
            cgpa: cgpa.toFixed(3),
        });
    };

    const createRow = (no) => ({
        no,
        subject: "",
        marks: "",
        grade: "",
        points: "",
        creditHours: "",
        gradePoints: "",
    });

    const resetCalculator = () => {
        const initialRows = [];
        for (let i = 1; i <= 6; i++) {
            initialRows.push(createRow(i));
        }
        setRows(initialRows);
        setCurrentGpa("");
        setTotalCreditHours("");
        setSummary({ sumCreditHours: 0, sumGradePoints: 0, semesterGpa: 0, cgpa: 0 });
    };

    return (
        <div className={styles.wrapper}>
            <h1>Grade Calculator</h1>

            {/* Current GPA + Hours */}
            <div className={styles.currentInputs}>
                <div>
                    <label>Previous GPA</label>
                    <input
                        type="number"
                        step="0.01"
                        value={currentGpa}
                        onChange={(e) => setCurrentGpa(e.target.value)}
                        placeholder="Enter previous GPA"
                    />
                </div>
                <div>
                    <label>Total Credit Hours</label>
                    <input
                        type="number"
                        value={totalCreditHours}
                        onChange={(e) => setTotalCreditHours(e.target.value)}
                        placeholder="Enter total credit hours"
                    />
                </div>
            </div>

            {/* Table */}
            <div className={styles.tbs}>
            <table className={styles.table}>
                <thead>
                <tr>
                    <th>No.</th>
                    <th>Subject</th>
                    <th>Marks</th>
                    <th>Grade</th>
                    <th>Points</th>
                    <th>Credit Hours</th>
                    <th>Grade Points</th>
                </tr>
                </thead>
                <tbody>
                {rows.map((row, i) => (
                    <tr key={i}>
                        <td>{row.no}</td>
                        <td>
                            <input
                                type="text"
                                value={row.subject}
                                placeholder="Enter subject"
                                onChange={(e) => handleRowChange(i, "subject", e.target.value)}
                            />
                        </td>
                        <td>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                placeholder="Marks"
                                value={row.marks}
                                onChange={(e) => handleRowChange(i, "marks", e.target.value)}
                            />
                        </td>
                        <td>
                            <input type="text" value={row.grade} readOnly />
                        </td>
                        <td>
                            <input type="text" value={row.points} readOnly />
                        </td>
                        <td>
                            <input
                                type="number"
                                min="0"
                                value={row.creditHours}
                                placeholder="Credit Hours"
                                onChange={(e) =>
                                    handleRowChange(i, "creditHours", e.target.value)
                                }
                            />
                        </td>
                        <td>
                            <input type="text" value={row.gradePoints} readOnly />
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>

            {/* Controls */}
            <div className={styles.controls}>
                <button onClick={resetCalculator} style={{ background: "#ff3b30" }}>
                    Reset Calculator
                </button>
                <button onClick={calculate}>Calculate</button>
            </div>

            {/* Summary */}
            <div className={styles.summary}>
                <div>
                    <label>Sum Credit Hours</label>
                    <input type="text" value={summary.sumCreditHours} readOnly />
                </div>
                <div>
                    <label>Sum Grade Points</label>
                    <input type="text" value={summary.sumGradePoints} readOnly />
                </div>
                <div>
                    <label>Semester GPA</label>
                    <input type="text" value={summary.semesterGpa} readOnly />
                </div>
                <div>
                    <label>CGPA</label>
                    <input type="text" value={summary.cgpa} readOnly />
                </div>
            </div>
        </div>
    );
};

export default GradeCalculator;
